import { createContext, useContext, useState, type ReactNode } from 'react';
import {
  login as doLogin,
  currentToken,
  logout as clearToken,
  decodeClaims,
  isExpired,
  type TokenClaims,
} from './authClient';

type Status = 'logged_out' | 'checking' | 'logged_in';

interface AuthState {
  status: Status;
  token: string | null;
  claims: TokenClaims | null;
  error: string | null;
  login: (email: string, password: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

function initialToken(): string | null {
  const t = currentToken();
  if (t && !isExpired(decodeClaims(t))) return t;
  clearToken();
  return null;
}

/** Guarda de rota da SPA inteira (ADR 0019 §1) — não tem página pública, então isso vira a raiz. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(initialToken);
  const [status, setStatus] = useState<Status>(token ? 'logged_in' : 'logged_out');
  const [error, setError] = useState<string | null>(null);

  const claims = token ? decodeClaims(token) : null;

  const value: AuthState = {
    status,
    token,
    claims,
    error,
    login: (email, password) => {
      setError(null);
      setStatus('checking');
      doLogin(email, password)
        .then(() => {
          setToken(currentToken());
          setStatus('logged_in');
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
          setStatus('logged_out');
        });
    },
    logout: () => {
      clearToken();
      setToken(null);
      setStatus('logged_out');
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fora do AuthProvider');
  return ctx;
}
